import type { ReactNode } from "react";
import { Link } from "react-router";
import { avatarInitials, AVATAR_PLACEHOLDER_STYLE } from "@/lib/avatar";

// Eigene Personen-Zeile statt PersonResultRow/Avatar wiederzuverwenden: die
// beiden gemeinsamen Primitive erwarten ein base64-eingebettetes Foto
// (avatarContent/avatarMime, so wie genesis Person-Avatare liefert). TMDB gibt
// stattdessen eine externe Bild-URL. Die produktiven Komponenten dafuer
// umzubauen waere ein System-Change fuer einen Bedarf, den nur der
// Tobis-Prototyp hat - dieselbe Abwaegung wie bei TobisTitleCard (eigene
// Kachel statt ShareItemCardFeed). Klassen/Groessen bewusst identisch zu
// Avatar/PersonResultRow, damit ein Wechsel spaeter rein mechanisch waere.

const SIZES = { md: "h-9 w-9 text-xs", lg: "h-10 w-10 text-sm" } as const;

export function TobisAvatar({
  name,
  photoUrl,
  size = "lg",
}: {
  name: string | null;
  photoUrl: string | null;
  size?: keyof typeof SIZES;
}) {
  const dim = SIZES[size];
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt=""
        loading="lazy"
        className={`${dim} shrink-0 rounded-full object-cover`}
      />
    );
  }
  return (
    <div
      className={`${dim} flex shrink-0 items-center justify-center rounded-full font-serif`}
      style={AVATAR_PLACEHOLDER_STYLE}
    >
      {avatarInitials(name)}
    </div>
  );
}

export function TobisPersonRow({
  name,
  photoUrl,
  title,
  sub,
  to,
  actions,
  embedded = false,
  dense = false,
}: {
  name: string | null;
  photoUrl: string | null;
  title: string;
  sub?: string | null;
  to?: string | null;
  actions?: ReactNode;
  embedded?: boolean;
  /** Kompakt-Ansicht (TobisTalentsPage): kleinerer Avatar, weniger Padding, mehr Zeilen pro Bildschirm. */
  dense?: boolean;
}) {
  const baseClass = `flex items-center gap-3 ${dense ? "p-2" : "p-3"} ${embedded ? "bg-page box-flat" : "bg-surface box"}`;
  const titleClass = `truncate text-[14px] text-ink/80${to ? " transition group-hover:text-accent" : ""}`;

  const inner = (
    <>
      <TobisAvatar name={name} photoUrl={photoUrl} size={dense ? "md" : "lg"} />
      <div className="min-w-0 flex-1">
        <div className={titleClass}>{title || "–"}</div>
        {sub ? <div className="truncate text-[12px] text-ink/45">{sub}</div> : null}
      </div>
      {actions ? (
        <div
          className="shrink-0"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {actions}
        </div>
      ) : null}
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        className={`${baseClass} group cursor-pointer transition hover:ring-accent/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30`}
      >
        {inner}
      </Link>
    );
  }
  return <div className={baseClass}>{inner}</div>;
}
